import React from 'react';
import { Card, Table, Tag, Empty, Spin, Typography } from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, setConfig } from '../../store/actions';
import { Helmet } from 'react-helmet';
import Config from '../../Config';

const { Text } = Typography;

const configClass = {
	title: 'Status Printer',
	apiUrl: { status: 'addons/api/printer/status/' },
};

const config = new Config();

const REFRESH_INTERVAL_MS = 60000;

const STATUS_META = {
	online: { label: 'Online', color: '#16a34a', bg: '#16a34a18', icon: '🟢' },
	perlu_perhatian: { label: 'Perlu Perhatian', color: '#d97706', bg: '#d9770618', icon: '🟡' },
	offline: { label: 'Offline', color: '#dc2626', bg: '#dc262618', icon: '🔴' },
	belum_pernah_terhubung: { label: 'Belum Pernah Terhubung', color: '#64748b', bg: '#64748b18', icon: '⚪' },
};

// Halaman monitoring (bukan CRUD) - status dihitung backend saat request
// (bukan disimpan), auto-refresh tiap 60 detik selama halaman ini terbuka.
// Ini kasus pertama di project yg butuh polling KONTINU (bukan one-shot
// task-poll spt sync tunggakan) - lihat catatan cleanup di componentWillUnmount.
class StatusPrinter extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			ringkasan: null,
			results: [],
			lastUpdated: null,
			err: '',
			isMobile: window.innerWidth <= 768,
		};
		this._refreshInterval = null;
	}

	UNSAFE_componentWillMount() { this.props.initHandler(); }

	componentDidMount() {
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);

		this.fetchStatus();
		this._refreshInterval = setInterval(this.fetchStatus, REFRESH_INTERVAL_MS);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
		if (this._refreshInterval) {
			clearInterval(this._refreshInterval);
			this._refreshInterval = null;
		}
	}

	fetchStatus = () => {
		this.setState((prev) => ({ loading: prev.results.length === 0, err: '' }));
		this.props.ajaxViewHandler('get', configClass.apiUrl.status)
			.then(() => {
				if (this.props.isSuccess) {
					const data = this.props.responseMsg || {};
					this.setState({
						ringkasan: data.ringkasan || null,
						results: data.results || [],
						lastUpdated: new Date(),
						loading: false,
					});
				} else {
					this.setState({ loading: false, err: 'Gagal memuat status printer.' });
				}
			})
			.catch(() => this.setState({ loading: false, err: 'Gagal memuat status printer.' }));
	};

	_kpiCard({ icon, label, value, accent }) {
		return (
			<div style={{ background: '#fff', borderRadius: 14, padding: '16px 20px', border: `1px solid ${accent}22`, boxShadow: `0 2px 12px ${accent}14`, display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 150px', minWidth: 0 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{icon}</div>
					<span style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: .8 }}>{label}</span>
				</div>
				<div style={{ fontSize: 20, fontWeight: 900, color: accent, fontFamily: 'monospace', lineHeight: 1.1 }}>{value}</div>
			</div>
		);
	}

	_fmtWaktu(value) {
		if (!value) return <Text style={{ color: '#94a3b8', fontSize: 12 }}>-</Text>;
		return <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{moment(value).format('DD-MM-YYYY HH:mm:ss')}</Text>;
	}

	_buildColumns() {
		return [
			{
				title: 'UPT', dataIndex: 'upt_nama', width: 180,
				render: (v) => <Text style={{ fontSize: 12, fontWeight: 600 }}>{v}</Text>,
			},
			{
				title: 'Printer', dataIndex: 'nama',
				render: (v, row) => (
					<div>
						<Text style={{ fontSize: 12, fontWeight: 600 }}>{v}</Text>
						<div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>{row.serial}</div>
					</div>
				),
			},
			{
				title: 'Tipe', dataIndex: 'tipe_nama', width: 100, align: 'center',
				render: (v) => <Tag>{v}</Tag>,
			},
			{
				title: 'Status', dataIndex: 'status', width: 170, align: 'center',
				render: (v) => {
					const meta = STATUS_META[v] || STATUS_META.belum_pernah_terhubung;
					return (
						<Tag style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}44`, fontWeight: 700, borderRadius: 8 }}>
							{meta.icon} {meta.label}
						</Tag>
					);
				},
			},
			{
				title: 'Antrian Pending', dataIndex: 'pending_count', width: 120, align: 'center',
				render: (v) => <Text style={{ fontFamily: 'monospace', fontWeight: 700, color: v > 0 ? '#d97706' : '#94a3b8' }}>{v}</Text>,
			},
			{
				title: 'Heartbeat Terakhir', dataIndex: 'last_heartbeat_at', width: 170,
				render: (v) => this._fmtWaktu(v),
			},
			{
				title: 'Cetak Sukses Terakhir', dataIndex: 'last_print_success_at', width: 170,
				render: (v) => this._fmtWaktu(v),
			},
		];
	}

	render() {
		const { loading, ringkasan, results, lastUpdated, err, isMobile } = this.state;

		return (
			<div>
				<Helmet>
					<title>{configClass.title + ' | ' + config.get_site_info().name}</title>
				</Helmet>

				<Card style={{ borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>
					<div style={{
						background: 'linear-gradient(135deg, #0f2847 0%, #1a3a6b 100%)', padding: '16px 20px',
						display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
							<div style={{ width: 38, height: 38, background: 'rgba(251,191,36,0.15)', borderRadius: 10,
								display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19 }}><PrinterOutlined style={{ color: '#fbbf24' }} /></div>
							<div>
								<div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Status Printer</div>
								<div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
									Monitoring heartbeat & antrian cetak kutipan/notice per UPT
								</div>
							</div>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: 11 }}>
							<ReloadOutlined spin={loading} />
							{lastUpdated ? `Update terakhir ${moment(lastUpdated).format('HH:mm:ss')}` : ''}
						</div>
					</div>

					<div style={{ padding: isMobile ? 14 : 22 }}>
						{loading && results.length === 0 ? (
							<div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
						) : err ? (
							<Empty description={err} style={{ padding: 40 }} />
						) : results.length === 0 ? (
							<Empty description="Belum ada printer terdaftar" style={{ padding: 40 }} />
						) : (
							<>
								<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
									{this._kpiCard({ icon: '🖨️', label: 'Total Printer', value: ringkasan?.total ?? 0, accent: '#0f2847' })}
									{this._kpiCard({ icon: '🟢', label: 'Online', value: ringkasan?.online ?? 0, accent: '#16a34a' })}
									{this._kpiCard({ icon: '🟡', label: 'Perlu Perhatian', value: ringkasan?.perlu_perhatian ?? 0, accent: '#d97706' })}
									{this._kpiCard({ icon: '🔴', label: 'Offline', value: ringkasan?.offline ?? 0, accent: '#dc2626' })}
									{this._kpiCard({ icon: '⚪', label: 'Belum Terhubung', value: ringkasan?.belum_pernah_terhubung ?? 0, accent: '#64748b' })}
								</div>

								<div style={{ overflowX: 'auto' }}>
									<Table
										rowKey="id"
										columns={this._buildColumns()}
										dataSource={results}
										pagination={false}
										size="small"
										scroll={{ x: 900 }}
									/>
								</div>
							</>
						)}
					</div>
				</Card>
			</div>
		);
	}
}

const mapStateToProps = (state) => state.configReducers;

export default connect(
	mapStateToProps,
	{ initHandler, setConfig, ajaxViewHandler }
)(StatusPrinter);
